import { type RatePair } from "@frontend/hooks/rates/useRateMatrix";

// 3CE brand colors — primary palette per design.
// Use these as the dominant fills, accents, and chart anchors.
// For body text on white, prefer the *_INK / *_DARK variants below — the
// canonical brand hexes hit ~3.4:1 against white, which is fine for fills,
// large display text, and UI component states (≥3:1) but fails AA for body
// text (<4.5:1). The ink variants share each color's hue with extra depth.
export const TEAL_3CE = "#61acd4";
export const AMBER = "#bb9ee5"; // burnt orange — current-rate / comparison anchor
export const YELLOW = "#FBC43C";
export const ENERGY_BLUE = "#2994D3";

// Dark / ink variants — AA-compliant text on white (≥4.5:1) and
// secondarily, hover-darker fills.
export const TEAL_3CE_DARK = "#398fbd";
export const TEAL_3CE_INK = "#61acd4";
export const AMBER_INK = "#c14213"; // dark burnt-orange for body text
export const ENERGY_BLUE_DARK = "#61acd4"; // hover + AA text variant of brand blue
export const YELLOW_INK = "#8a5a00"; // dark amber for body text on white

// Soft tints — backgrounds for badges, pills, callouts.
export const TEAL_3CE_SOFT = "#f2f9fc";
export const AMBER_SOFT = "#f0edf5";
export const YELLOW_SOFT = "#e1eef5";
export const ENERGY_BLUE_SOFT = "#e6f2fa";

// Borders — visible on white, not for text.
export const AMBER_BORDER = "#d7eefa"; // yellow-tinted warm border around the current-rate badge
export const YELLOW_BORDER = "#d7eefa";

// Chart-anchor neutral — a deep variant of brand blue so the chart story
// reads as one palette instead of "brand + isolated navy".
export const NAVY = "#15446b";

// Status semantics (intentionally non-brand: green/red carry universal meaning).
export const GREEN_GOOD = "#10884f";
export const GREEN_SOFT = "#e7f4ec";
export const RED_BAD = "#b3321b";
export const RED_SOFT = "#fde8e3";

// Neutrals & structure
export const DARK_GRAY = "#2a2a2a";
export const MERCURY = "#e6e6e6";
export const CONTENT_BG = "#fcfcfd";
export const FIELD_BORDER = "#a3a7a9";

// Secondary chart-only swatches — used when there are more rate pairs than
// brand slots in CHART_PALETTE. Kept distinct from RED_BAD/GREEN_GOOD so
// "alternative rate" hues never read as a status indicator.
export const MAGENTA = "#b8336a";
export const SKY = "#a7c8c9";
export const RED = "#a8341e";

// Shadows
export const CARD_SHADOW =
  "0px 1px 1.5px rgba(16, 24, 40, 0.1), 0px 1px 1px rgba(16, 24, 40, 0.06)";
export const BUTTON_SHADOW = "0px 1px 2px rgba(16, 24, 40, 0.05)";

// Brand-first chart palette: NAVY anchors the current rate, then the four
// brand colors lead, then secondaries fill the long tail.
export const CHART_PALETTE = [
  NAVY,
  AMBER,
  TEAL_3CE,
  ENERGY_BLUE,
  YELLOW,
  MAGENTA,
  SKY,
  "#6e9a4a",
  RED,
];

export const pairKey = (p: RatePair) =>
  `${p.iou_rate_code}|${p.ccce_rate_code}`;

export const buildPairColorMap = (
  matrix: RatePair[],
): Record<string, string> => {
  const map: Record<string, string> = {};
  if (!matrix.length) return map;

  const current = matrix.find((p) => p.isCurrent);
  if (current) map[pairKey(current)] = NAVY;

  const others = matrix
    .filter((p) => !p.isCurrent)
    .sort((a, b) => {
      if (a.ccce_rate_code !== b.ccce_rate_code) {
        return a.ccce_rate_code.localeCompare(b.ccce_rate_code);
      }
      return a.iou_rate_code.localeCompare(b.iou_rate_code);
    });

  const remaining = CHART_PALETTE.filter((c) => c !== NAVY);
  others.forEach((p, i) => {
    map[pairKey(p)] = remaining[i % remaining.length];
  });

  return map;
};
